import { LifestyleMainPageTemplateProps } from "./template";
import { useNavigate } from "react-router-dom";
import { useState, useContext, useEffect, useRef, useMemo } from "react";
import { AuthContext } from "@auth";
import { useGetGroupLifestyleAnalytics, useGetLifestyleGroup, useGetPartnerPaymentMethods, useGetLifestyleTransactions } from "@queries";
import { useApproveLifestyleTransaction, useRejectLifestyleTransaction } from "@mutations";
import { lifestyleCategories } from "../categories/categories-data";
import { 
  TimeRangeFilter, 
  LSAMetrics, 
  CategorySpending, 
  SpendingTrend, 
  PendingTransaction, 
  BudgetUtilization, 
  EngagementInsights,
  LifestyleGroup,
  LifestylePeriodAnalytics,
  LifestyleAnalyticsResponse,
  LSAGroup
} from "../../../../types";
import { toast } from "sonner";
import { createApproveTransactionHandler, createRejectTransactionHandler } from "../transaction-actions";

// Helper function to get category info by ID
const getCategoryInfo = (categoryId: string) => {
    // Find category by matching title (case-insensitive)
    const category = lifestyleCategories.find(cat => 
        cat.name.toLowerCase().replace(/[^a-z0-9]/g, '') === categoryId.toLowerCase().replace(/[^a-z0-9]/g, '') ||
        cat.name.toLowerCase() === categoryId.toLowerCase()
    );
    return category || { icon: "📊", name: categoryId };
};

export const useLifestyleMainPage = (): LifestyleMainPageTemplateProps => {
    const navigate = useNavigate();
    const { 
        partner, 
        lifestyleAnalytics, 
        lifestyleGroup, 
        billingDetails,
        lifestyleSelectedGroupId,
        setLifestyleSelectedGroupId,
        lifestyleSelectedTimeRange,
        setLifestyleSelectedTimeRange,
        creditCardStatus
    } = useContext(AuthContext);
    const { getGroupLifestyleAnalytics, loading, error } = useGetGroupLifestyleAnalytics();
    const { getLifestyleGroup, loading: groupLoading, error: groupError } = useGetLifestyleGroup();
    const approveLifestyleTransaction = useApproveLifestyleTransaction();
    const rejectLifestyleTransaction = useRejectLifestyleTransaction();
    const { getPartnerPaymentMethods, creditCards } = useGetPartnerPaymentMethods();
    const { getLifestyleTransactions, transactions: rawHistoryTransactions, loading: historyLoading } = useGetLifestyleTransactions();
    const partnerHasLsaGroups = partner?.lsaGroups && partner?.lsaGroups.length > 0;
    const [isSilentRefresh, setIsSilentRefresh] = useState(false);
    const silentRefreshRef = useRef(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const initialGroupId = lifestyleSelectedGroupId || (partnerHasLsaGroups ? partner?.lsaGroups?.[0]?.groupId ?? "" : "");
    const [showLoader, setShowLoader] = useState(Boolean(initialGroupId));
    const [historyTransactions, setHistoryTransactions] = useState<PendingTransaction[]>([]);
    
    const [hasLifestyleFund, setHasLifestyleFund] = useState(partnerHasLsaGroups ?? false);
    const [selectedGroupId, setSelectedGroupIdState] = useState<string>(initialGroupId);
    const [currentAnalytics, setCurrentAnalytics] = useState<LifestylePeriodAnalytics | null>(null);
    const [selectedGroupDetails, setSelectedGroupDetails] = useState<LSAGroup | null>(null);
    const [timeRange, setTimeRangeState] = useState<TimeRangeFilter>(lifestyleSelectedTimeRange || TimeRangeFilter.YEAR_TO_DATE);
    const [approvingTransactionIds, setApprovingTransactionIds] = useState<Set<string>>(new Set());
    const [rejectingTransactionIds, setRejectingTransactionIds] = useState<Set<string>>(new Set());
    const coveredUsers = partner?.premiumSettings?.[0]?.users || [];
    const coveredUserLookup = useMemo(() => {
        const map = new Map<string, any>();
        coveredUsers.forEach((user: any) => {
            if (!user) return;
            if (user.userId) {
                map.set(user.userId, user);
            }
            if (user.emailAddress) {
                map.set(String(user.emailAddress).toLowerCase(), user);
            }
        });
        return map;
    }, [coveredUsers]);
    // Store fetched analytics data
    //const [analytics, setAnalytics] = useState<LifestyleAnalyticsResponse | null>(null);

    // Lifestyle groups
    const lifestyleGroups: LifestyleGroup[] = partner?.lsaGroups?.map(group => ({
        id: group.groupId,
        name: group.groupName,
        status: "active" as const
    })) || [];

    useEffect(() => {
        if (lifestyleGroup) {
            setSelectedGroupDetails(lifestyleGroup);
        }
    }, [lifestyleGroup]);

    useEffect(() => {
        if (!selectedGroupId && partnerHasLsaGroups) {
            const fallbackId = partner?.lsaGroups?.[0]?.groupId;
            if (fallbackId) {
                setSelectedGroupIdState(fallbackId);
                setLifestyleSelectedGroupId(fallbackId);
            }
        }
    }, [partnerHasLsaGroups, partner?.lsaGroups, selectedGroupId, setLifestyleSelectedGroupId]);

    useEffect(() => {
        if (lifestyleSelectedGroupId && lifestyleSelectedGroupId !== selectedGroupId) {
            setSelectedGroupIdState(lifestyleSelectedGroupId);
        }
    }, [lifestyleSelectedGroupId, selectedGroupId]);

    useEffect(() => {
        if (lifestyleSelectedTimeRange && lifestyleSelectedTimeRange !== timeRange) {
            setTimeRangeState(lifestyleSelectedTimeRange);
        }
    }, [lifestyleSelectedTimeRange, timeRange]);

    // Fetch data when group changes
    useEffect(() => {
        if (selectedGroupId) {
            getGroupLifestyleAnalytics({
                variables: { groupId: selectedGroupId }
            });
            getLifestyleGroup({ variables: { groupId: selectedGroupId } });
            getLifestyleTransactions({
                variables: {
                    status: undefined,
                    offset: 0,
                    limit: 200
                }
            });
            setHasLoadedOnce(false);
        }
    }, [selectedGroupId, getGroupLifestyleAnalytics, getLifestyleGroup, getLifestyleTransactions]);

    useEffect(() => {
        if (lifestyleAnalytics && lifestyleGroup && lifestyleGroup.id === selectedGroupId) {
            setHasLoadedOnce(true);
        }
    }, [lifestyleAnalytics, lifestyleGroup, selectedGroupId]);

    const navigateToPage = (to: string) => {
        navigate(to);
    }

    const setSelectedGroupId = (groupId: string) => {
        setSelectedGroupIdState(groupId);
        setLifestyleSelectedGroupId(groupId);
    };

    const setTimeRange = (range: TimeRangeFilter) => {
        setTimeRangeState(range);
        setLifestyleSelectedTimeRange(range);
    };

    const onReturnPressed = () => {
        navigate(-1);
    }

    const toggleDesign = () => {
        setHasLifestyleFund(!hasLifestyleFund);
    }

    useEffect(() => {
        const isSilent = isSilentRefresh || silentRefreshRef.current;
        if (loading) {
            if (!isSilent) {
                setShowLoader(true);
            }
        } else if (!hasLoadedOnce) {
            setShowLoader(true);
        } else {
            setShowLoader(false);
            if (isSilent) {
                silentRefreshRef.current = false;
                setIsSilentRefresh(false);
            }
        }
    }, [loading, isSilentRefresh, hasLoadedOnce]);

    const refreshData = (options?: { silent?: boolean }) => {
        const silent = options?.silent ?? false;
        if (silent) {
            setIsSilentRefresh(true);
            silentRefreshRef.current = true;
        }
        if (selectedGroupId) {
            getGroupLifestyleAnalytics({
                variables: { groupId: selectedGroupId }
            });
            getLifestyleGroup({ variables: { groupId: selectedGroupId } });
        }
    }

    const resolvePaymentId = async (): Promise<string | null> => {
        const existing = billingDetails?.creditCards?.[0]?.paymentId ?? creditCards?.[0]?.paymentId;
        if (existing) {
            return existing;
        }
        try {
            const result = await getPartnerPaymentMethods();
            const fetched = (result as any)?.data?.getPartnerPaymentMethods?.[0]?.paymentId;
            return fetched ?? null;
        } catch (err) {
            console.error("Error fetching partner payment methods:", err);
            return null;
        }
    };

    useEffect(() => {
        if (!rawHistoryTransactions) {
            return;
        }
        try {
            const { docs } = rawHistoryTransactions;
            const parsedDocs = JSON.parse(docs);
            const filtered = parsedDocs.filter((doc: any) => {
                const status = (doc.status || "").toLowerCase();
                return status === "approved" || status === "completed" || status === "rejected";
            });

            const now = new Date();
            const isWithinRange = (dateString: string): boolean => {
                const date = new Date(dateString);
                if (Number.isNaN(date.getTime())) return false;

                switch (timeRange) {
                    case TimeRangeFilter.LAST_7_DAYS: {
                        const cutoff = new Date(now);
                        cutoff.setDate(now.getDate() - 7);
                        return date >= cutoff;
                    }
                    case TimeRangeFilter.LAST_30_DAYS: {
                        const cutoff = new Date(now);
                        cutoff.setDate(now.getDate() - 30);
                        return date >= cutoff;
                    }
                    case TimeRangeFilter.YEAR_TO_DATE: {
                        const startOfYear = new Date(now.getFullYear(), 0, 1);
                        return date >= startOfYear;
                    }
                    case TimeRangeFilter.LAST_12_MONTHS: {
                        const cutoff = new Date(now);
                        cutoff.setMonth(now.getMonth() - 12);
                        return date >= cutoff;
                    }
                    default:
                        return true;
                }
            };

            const mapped: PendingTransaction[] = filtered
                .filter((doc: any) => {
                    const submittedDate = doc.receiptDate || doc.createdAt || new Date().toISOString();
                    return isWithinRange(submittedDate);
                })
                .map((doc: any) => {
                    const userFromContext = (doc.userId && coveredUserLookup.get(doc.userId)) || (doc.emailAddress ? coveredUserLookup.get(String(doc.emailAddress).toLowerCase()) : undefined);
                    const nameParts = [
                        doc.firstName || userFromContext?.firstName,
                        doc.lastName || userFromContext?.lastName
                    ].filter(Boolean);
                    const memberName = nameParts.join(" ").trim() || userFromContext?.emailAddress || doc.emailAddress || "Unknown User";
                    const amountCents = doc.amount || doc.coveredAmount?.value || doc.costAmount || 0;
                    const submittedDate = doc.receiptDate || doc.createdAt || new Date().toISOString();
                    return {
                        id: doc.id || doc.transactionId || `txn-${Date.now()}`,
                        memberName,
                        amount: amountCents / 100,
                        category: doc.categoryId || "Unknown Category",
                        submittedDate,
                        status: (doc.status || "completed") as 'pending' | 'approved' | 'rejected',
                        companyName: doc.brandName || "",
                    };
                })
                .sort((a: PendingTransaction, b: PendingTransaction) => {
                    const dateA = new Date(a.submittedDate).getTime();
                    const dateB = new Date(b.submittedDate).getTime();
                    return dateB - dateA;
                });
            setHistoryTransactions(mapped);
        } catch (err) {
            console.error("Error parsing history transactions:", err);
            setHistoryTransactions([]);
        }
    }, [rawHistoryTransactions, timeRange, coveredUserLookup]);

    const handleApprovePendingTransaction = createApproveTransactionHandler({
        resolvePaymentId,
        approveMutation: approveLifestyleTransaction,
        onStart: (transactionId) => {
            setApprovingTransactionIds((prev) => {
                const next = new Set(prev);
                next.add(transactionId);
                return next;
            });
        },
        onSuccess: async () => {
            refreshData({ silent: true });
            await getLifestyleTransactions({
                variables: {
                    status: undefined,
                    offset: 0,
                    limit: 200
                }
            });
        },
        onError: (message) => toast.error(message),
        onFinally: (transactionId) => {
            setApprovingTransactionIds((prev) => {
                const next = new Set(prev);
                next.delete(transactionId);
                return next;
            });
        }
    });

    const handleRejectPendingTransaction = createRejectTransactionHandler({
        rejectMutation: rejectLifestyleTransaction,
        onStart: (transactionId) => {
            setRejectingTransactionIds((prev) => {
                const next = new Set(prev);
                next.add(transactionId);
                return next;
            });
        },
        onSuccess: async () => {
            refreshData({ silent: true });
            await getLifestyleTransactions({
                variables: {
                    status: undefined,
                    offset: 0,
                    limit: 200
                }
            });
        },
        onError: (message) => toast.error(message),
        onFinally: (transactionId) => {
            setRejectingTransactionIds((prev) => {
                const next = new Set(prev);
                next.delete(transactionId);
                return next;
            });
        }
    });

    // Get current analytics for time range
    const getCurrentAnalytics = (): LifestylePeriodAnalytics | null => {
        if (!lifestyleAnalytics) return null;
        
        switch (timeRange) {
            case TimeRangeFilter.LAST_7_DAYS:
                return lifestyleAnalytics.sevenDays;
            case TimeRangeFilter.LAST_30_DAYS:
                return lifestyleAnalytics.thirtyDays;
            case TimeRangeFilter.YEAR_TO_DATE:
                return lifestyleAnalytics.yearToDate;
            case TimeRangeFilter.LAST_12_MONTHS:
                return lifestyleAnalytics.oneYear;
            default:
                return lifestyleAnalytics.thirtyDays;
        }
    };
    useEffect(() => {
        setCurrentAnalytics(getCurrentAnalytics());
    }, [lifestyleAnalytics, timeRange]);
    // Transform data

    const getSpendingTrendsPeriod = (analytics: LifestylePeriodAnalytics, timeRange: TimeRangeFilter): SpendingTrend[] => {
        switch (timeRange) {
            case TimeRangeFilter.LAST_7_DAYS:
                return analytics.dailySavings?.map(daily => ({
                    date: daily.date,
                    amount: daily.amount / 100 // Convert cents to dollars
                }));
        
        case TimeRangeFilter.LAST_30_DAYS:
            return analytics.dailySavings?.map(daily => ({
                date: daily.date,
                amount: daily.amount / 100 // Convert cents to dollars
            }));
        case TimeRangeFilter.YEAR_TO_DATE:
            return analytics.weeklySavings?.map(daily => ({
                date: daily.weekStart,
                amount: daily.amount / 100 // Convert cents to dollars
            }));
        case TimeRangeFilter.LAST_12_MONTHS:
            return analytics.monthlySavings?.map(daily => ({
                date: daily.month,
                amount: daily.amount / 100 // Convert cents to dollars
            }));
        default:
            return analytics.dailySavings?.map(daily => ({
                date: daily.date,
                amount: daily.amount / 100 // Convert cents to dollars
            }));
        }
    }

    const categorySpending: CategorySpending[] = currentAnalytics 
        ? currentAnalytics.categoryBreakdown.map(cat => {
            const categoryInfo = getCategoryInfo(cat.categoryId || 'Unknown Category');
            return {
                category: categoryInfo.name,
                amount: cat.amount / 100 // Convert cents to dollars
            };
        })
        : [];

    const spendingTrends: SpendingTrend[] = currentAnalytics 
        ? getSpendingTrendsPeriod(currentAnalytics, timeRange)
        : [];

    const pendingTransactions: PendingTransaction[] = currentAnalytics 
        ? currentAnalytics.pendingTransactions.map(txn => ({
            id: txn.transactionId || `txn-${Date.now()}`,
            memberName: `${txn.firstName || ''} ${txn.lastName || ''}`.trim() || 'Unknown User',
            amount: txn.amount / 100, // Convert cents to dollars
            category: txn.categoryId || 'Unknown Category',
            submittedDate: txn.date || new Date().toISOString(),
            status: txn.status as 'pending' | 'approved' | 'rejected',
            companyName: txn.brandName || '',
            // receiptUrl: txn.receiptUrl // todo: can we get a receipt URL here?
        }))
        : [];

    const budgetUtilization: BudgetUtilization = lifestyleAnalytics && currentAnalytics 
        ? {
            totalBudget: lifestyleAnalytics.yearToDate.totalSpent + lifestyleAnalytics.yearToDate.totalBudgetRemaining,
            usedBudget: lifestyleAnalytics.yearToDate.totalSpent,
            remainingBudget: lifestyleAnalytics.yearToDate.totalBudgetRemaining,
            utilizationPercentage: (lifestyleAnalytics.yearToDate.totalSpent + lifestyleAnalytics.yearToDate.totalBudgetRemaining) > 0 
                ? Math.round((lifestyleAnalytics.yearToDate.totalSpent / (lifestyleAnalytics.yearToDate.totalSpent + lifestyleAnalytics.yearToDate.totalBudgetRemaining)) * 100) 
                : 0,
            recentCompletedTransactions: currentAnalytics?.completedTransactions.slice(0, 3).map(txn => ({
                id: txn.transactionId || `completed-${Date.now()}`,
                memberName: `${txn.firstName || ''} ${txn.lastName || ''}`.trim() || 'Unknown User',
                amount: txn.amount / 100, // Convert cents to dollars
                companyName: txn.brandName || '',
                completedDate: txn.date || new Date().toISOString()
            }))
        }
        : {
            totalBudget: 0,
            usedBudget: 0,
            remainingBudget: 0,
            utilizationPercentage: 0,
            recentCompletedTransactions: []
        };

    const engagementInsights: EngagementInsights = currentAnalytics 
        ? {
            mostActiveMembers: currentAnalytics.topUsers.map(user => ({
                userId: user.userId,
                totalAmount: user.totalSaved / 100 // Convert cents to dollars
            })),
            mostPopularCategories: currentAnalytics.topCategories.map(cat => {
                const categoryInfo = getCategoryInfo(cat.categoryId || 'Unknown Category');
                return {
                    category: categoryInfo.name,
                    amount: cat.amount / 100 // Convert cents to dollars
                };
            }),
            avgReimbursementAmount: currentAnalytics.averageSavedPerMember.coveredAmount / 100 || 0
        }
        : {
            mostActiveMembers: [],
        mostPopularCategories: [],
        avgReimbursementAmount: 0
    };

    console.log(currentAnalytics?.completedTransactions?.length);

    // Calculate metrics
    const totalReimbursed = currentAnalytics?.totalSaved?.coveredAmount ? currentAnalytics?.totalSaved?.coveredAmount / 100 : 0;
    const avgPerMember = currentAnalytics?.averageSavedPerMember?.coveredAmount ? currentAnalytics?.averageSavedPerMember?.coveredAmount / 100 : 0;
    const participationRate = Math.round(currentAnalytics?.participationRate || 0);
    const totalMembers = selectedGroupDetails?.membersCount || 0;
    const activeMembers = Math.round(totalMembers * (currentAnalytics?.participationRate || 0));

    const lsaMetrics: LSAMetrics = {
        totalReimbursed,
        avgPerMember,
        participationRate,
        budgetUtilization: budgetUtilization.utilizationPercentage,
        totalMembers,
        activeMembers
    };

    const exportData = (format: 'csv' = 'csv') => {
        if (format === 'csv') {
            const csvContent = [
                'Metric,Value',
                `Total Reimbursed,${lsaMetrics.totalReimbursed}`,
                `Average per Member,${lsaMetrics.avgPerMember}`,
                `Participation Rate,${lsaMetrics.participationRate}%`,
                `Budget Utilization,${lsaMetrics.budgetUtilization}%`,
                '',
                'Category,Amount',
                ...categorySpending.map(cat => `${cat.category},${cat.amount}`)
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `lifestyle-analytics-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        }
    };

    return {
        navigateToPage,
        onReturnPressed,
        hasLifestyleFund,
        toggleDesign,
        employeeSpendingData: categorySpending.map(cat => ({
            category: cat.category,
            amount: cat.amount,
            percentage: 0
        })),
        averageSavings: lsaMetrics.avgPerMember,
        topCategories: categorySpending.slice(0, 5).map(cat => cat.category),
        lifestyleGroupName: selectedGroupDetails?.name || "Default Group",
        coveredUsers,
        lifestyleGroups,
        selectedGroupId,
        setSelectedGroupId,
        selectedGroupDetails,
        timeRange,
        setTimeRange,
        lsaMetrics,
        categorySpending,
        spendingTrends,
        pendingTransactions,
        historyTransactions,
        budgetUtilization,
        engagementInsights,
        exportData,
        onApprovePendingTransaction: handleApprovePendingTransaction,
        onRejectPendingTransaction: handleRejectPendingTransaction,
        approvingTransactionIds,
        rejectingTransactionIds,
        isLoading: showLoader,
        isInitialLoad: showLoader && !hasLoadedOnce,
        error,
        isDataLoaded: !loading && !!lifestyleAnalytics && !!lifestyleGroup,
        refreshData,
        historyLoading,
        creditCardStatus
    }
}
